// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface MatchEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  match: any;
  courts: any[];
  referees: any[];
  onSave: () => void;
}

const MatchEditDialog: React.FC<MatchEditDialogProps> = ({
  open,
  onOpenChange,
  match,
  courts,
  referees,
  onSave
}) => {
  const [formData, setFormData] = useState({
    scheduled_date: '',
    scheduled_time: '',
    court_field: '',
    referee_id: '',
    referee2_id: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (match) {
      const scheduledDateTime = match.scheduled_time ? new Date(match.scheduled_time) : null;
      setFormData({
        scheduled_date: scheduledDateTime ? scheduledDateTime.toISOString().split('T')[0] : '',
        scheduled_time: scheduledDateTime ? scheduledDateTime.toTimeString().substring(0, 5) : '',
        court_field: match.court_field || '',
        referee_id: match.referee_id || '',
        referee2_id: match.referee2_id || '',
      });
    }
  }, [match]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Combine date and time
      let scheduled_time = null;
      if (formData.scheduled_date && formData.scheduled_time) {
        scheduled_time = `${formData.scheduled_date}T${formData.scheduled_time}:00`;
      }

      const { error } = await supabase
        .from('tournament_matches')
        .update({
          scheduled_time,
          court_field: formData.court_field || null,
          referee_id: formData.referee_id || null,
          referee2_id: formData.referee2_id || null,
        })
        .eq('id', match.id);

      if (error) throw error;

      toast.success('Match updated successfully');
      onSave();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating match:', error);
      toast.error(error.message || 'Failed to update match');
    } finally {
      setSaving(false);
    }
  };

  if (!match) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Match</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Match Info (read-only) */}
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-medium">
              {match.team1?.name || match.team1_name || 'TBD'} vs {match.team2?.name || match.team2_name || 'TBD'}
            </p>
            <p className="text-sm text-muted-foreground">
              Round {match.round_number} • Match {match.match_number}
            </p>
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label>Date</Label>
            <Input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
            />
          </div>

          {/* Time */}
          <div className="space-y-2">
            <Label>Time</Label>
            <Input
              type="time"
              value={formData.scheduled_time}
              onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
            />
          </div>

          {/* Court */}
          <div className="space-y-2">
            <Label>Court / Field</Label>
            <Select
              value={formData.court_field}
              onValueChange={(value) => setFormData({ ...formData, court_field: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select court" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No court assigned</SelectItem>
                {courts.map((court) => (
                  <SelectItem key={court.id || court.name} value={court.name}>
                    {court.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Primary Referee */}
          <div className="space-y-2">
            <Label>Primary Referee</Label>
            <Select
              value={formData.referee_id}
              onValueChange={(value) => setFormData({ ...formData, referee_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select referee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No referee assigned</SelectItem>
                {referees.map((ref) => (
                  <SelectItem key={ref.id} value={ref.id}>
                    {ref.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Secondary Referee */}
          <div className="space-y-2">
            <Label>Secondary Referee (Optional)</Label>
            <Select
              value={formData.referee2_id}
              onValueChange={(value) => setFormData({ ...formData, referee2_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select referee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {referees.map((ref) => (
                  <SelectItem key={ref.id} value={ref.id}>
                    {ref.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MatchEditDialog;
